import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo, useState } from 'react';
import { Input, Modal, ModalBody, ModalHeader } from 'reactstrap';
import PDFViewer from '../../../../components/Image/PDF';
import { WrapperField } from '../../../../components/InputField/WrapperField';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import { InputLabel } from '../components/IndividualLite/UserDetailsForm';
import { collectVisibleFieldErrors, getFieldErrorValue, omitFieldPathErrors, WIZARD, WIZARD_NUM } from './helperV2';
import ManagedAccountTncConsent from './ManagedAccountTncConsent';
import { areAllManagedAccountTncAccepted, toggleManagedAccountTncDoc } from './managedAccountTnc';
import { useNavigate } from 'react-router-dom';
import { doGET } from '../../../../util/HttpUtil';
import DocumentFirstStepHead from './components/DocumentFirstStepHead';
import { VARIANT } from '../../../../util/variant';
import { finalizeBusinessV2Submission } from './submitBusinessV2';

const SelfDeclarationV2 = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    handleChange,
    applicationForm,
    formId,
    wizard,
    handlePrev,
    pageNum = WIZARD_NUM.SelfDeclaration,
    hideBackButton = false,
    emitFieldChangeDetails = false,
    // Managed-account (broker "complete application") Terms & Conditions. Passed in only
    // from the broker terms step; empty for Business V2 onboarding, so that flow is
    // unaffected. When non-empty, every document must be accepted to advance.
    managedAccountTncDocuments = [],
    variant,
    stepCount,
    stepLabel,
}) => {
    const isDocFirst = variant === VARIANT.V2;
    const { showError } = useContext(DialogContext);
    const navigate = useNavigate();
    const [showBeneficialOwnersModal, setShowBeneficialOwnersModal] = useState(false);
    const [certificateRequestVersion, setCertificateRequestVersion] = useState(0);
    // Local Next-button spinner while the certificate is generated + the step advances
    // (the parent's `loading` doesn't cover the cert API call made here).
    const [submitting, setSubmitting] = useState(false);
    const beneficialOwnerCertificationField = cyclosFields?.["tnc"];
    const shouldShowBeneficialOwnerCertification = Boolean(beneficialOwnerCertificationField);
    const hasManagedAccountTnc = Array.isArray(managedAccountTncDocuments) && managedAccountTncDocuments.length > 0;
    const managedAccountAcceptedDocs = Array.isArray(data?.managedAccountTncAcceptedDocs)
        ? data.managedAccountTncAcceptedDocs
        : [];
    const allManagedAccountAccepted = areAllManagedAccountTncAccepted(managedAccountAcceptedDocs, managedAccountTncDocuments);
    const managedAccountTncError = getFieldErrorValue(errors || {}, "managedAccountTnc");

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => ({
            ...(prevErrors || {}),
            [field]: errorMsg,
        }));
    };

    const checkNext = () => {
        const stepFields = [
            "acceptDelarationAggrement",
            "signature",
            "signerFirstName",
            "signerMiddleName",
            "signerLastName",
        ];
        if (shouldShowBeneficialOwnerCertification) {
            stepFields.push("tnc");
        }
        const stepConfigs = stepFields.map((field) => cyclosFields?.[field]).filter(Boolean);
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields: stepConfigs,
            errors,
            type: Number(applicationForm?.type),
        });
        const mergedErrors = {
            ...(errors || {}),
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const fieldPath of stepFields) {
            const fieldError = getFieldErrorValue(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                showError(fieldError);
                return false;
            }
        }

        if (hasManagedAccountTnc && !allManagedAccountAccepted) {
            const message = "Please accept the Terms & Conditions for each document to continue.";
            setHasError(true);
            handleErrors("managedAccountTnc", message);
            showError(message);
            return false;
        }

        setErrors((prevErrors) => omitFieldPathErrors(prevErrors || {}, [...stepFields, "managedAccountTnc"]));
        return true;
    };

    const handleSubmit = async () => {
        if (!checkNext()) return undefined;

        setHasError(false);
        setSubmitting(true);
        try {
            // The applicant may certify the Certificate of Beneficial Owners (the `tnc`
            // checkbox) without ever opening its preview — most broker primary users won't.
            // So on Next we generate and persist the certificate against the application form
            // here too (the same endpoint used at final submit). Best-effort: a failure must
            // not block the applicant from proceeding to KYC.
            const certFormId = applicationForm?._id || formId || data?._id || "";
            if (shouldShowBeneficialOwnerCertification && certFormId) {
                try {
                    await doGET(`/api/application-doc/cert/beneficial-owners?form_id=${certFormId}`);
                } catch (error) {
                    console.log("Certificate generation error:", error);
                }
            }

            const nextStep = applicationForm?.getNextStepForForm?.({
                wizard,
                applicationForm,
                data,
            });
            // When no payment step follows (e.g. type 3 upgrades, where PayNow is
            // dropped), Terms & Conditions is the final step — so trigger the final
            // submit here, mirroring how DocumentsV2 finalises when it is last.
            const isLastStep = nextStep?.wizard === WIZARD.ThankYou;
            if (isLastStep) {
                // Type 3 (upgrade) has no payment step, so Terms & Conditions is the final
                // step. Reuse the exact same final-submission flow as the payment step:
                // validate required documents → submit → redirect to the post-submit page.
                return await finalizeBusinessV2Submission({
                    editId: certFormId,
                    data,
                    applicationForm,
                    nextStep,
                    type: applicationForm?.type,
                    // TnC is the last step here, so persist its own data via
                    // /api/business/update before submitting (payment step doesn't,
                    // because the step before it already saved).
                    skipUpdate: false,
                    handleUpdate,
                    setHasError,
                    showError,
                    navigate,
                });
            }
            return await handleUpdate({ nextStep });
        } finally {
            setSubmitting(false);
        }
    };

    const handleFieldChange = (fieldPath, value, nextData) => {
        if (typeof handleChange !== "function") return;
        if (emitFieldChangeDetails) {
            handleChange(fieldPath, value, nextData);
            return;
        }
        handleChange(nextData);
    };

    const handleManagedAccountTncToggle = (file, checked) => {
        const nextDocs = toggleManagedAccountTncDoc(managedAccountAcceptedDocs, file, checked, managedAccountTncDocuments);
        const allAccepted = areAllManagedAccountTncAccepted(nextDocs, managedAccountTncDocuments);
        if (checked) handleErrors("managedAccountTnc", "");
        handleFieldChange("managedAccountTncAcceptedDocs", nextDocs, {
            ...data,
            managedAccountTncAcceptedDocs: nextDocs,
            managedAccountTncAccepted: allAccepted,
        });
    };

    const beneficialOwnerCertificationLabel = beneficialOwnerCertificationField?.label
        || "I agree to the information provided in the Certificate of Beneficial Owners, is accurate and complete to the best of my knowledge.";
    const termsAcknowledgementError = getFieldErrorValue(errors || {}, "acceptDelarationAggrement");
    const beneficialOwnerCertificationError = getFieldErrorValue(errors || {}, "tnc");
    const certificateFormId = applicationForm?._id || formId || data?._id || "";
    const beneficialOwnersCertificateSrc = useMemo(
        () => certificateFormId
            ? `/api/application-doc/cert/beneficial-owners?form_id=${certificateFormId}&v=${certificateRequestVersion}`
            : "",
        [certificateFormId, certificateRequestVersion],
    );
    const certificationTextParts = useMemo(() => {
        const marker = "Certificate of Beneficial Owners";
        const rawLabel = String(beneficialOwnerCertificationLabel || "");
        const markerIndex = rawLabel.indexOf(marker);

        if (markerIndex < 0) {
            return {
                before: "I agree to the information provided in the ",
                after: ", is accurate and complete to the best of my knowledge.",
            };
        }

        return {
            before: rawLabel.slice(0, markerIndex),
            after: rawLabel.slice(markerIndex + marker.length),
        };
    }, [beneficialOwnerCertificationLabel]);

    return (
        <div className="business-v2-step-shell">
            {isDocFirst ? (
                <DocumentFirstStepHead
                    stepIndex={pageNum}
                    stepCount={stepCount}
                    stepLabel={stepLabel}
                    title={stepLabel || 'Review and confirm'}
                    intro='Read the declarations, then confirm the application is accurate.'
                    dataTestId='business-v2-self-declaration-head'
                />
            ) : null}
            <div className='business-v2-step-form-stack'>
                <div className='flex-1'>
                    <WrapperField
                        disabled={asLabel}
                        jsonField={cyclosFields["acceptDelarationAggrement"]}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleErrors('acceptDelarationAggrement', errorMsg)}
                    />
                    {termsAcknowledgementError && hasError ? (
                        <div className="ps-0 mt-0 pb-2 ms-0 w-100 error__message left-0 mb02">
                            {termsAcknowledgementError}
                        </div>
                    ) : null}
                </div>

                {hasManagedAccountTnc ? (
                    <ManagedAccountTncConsent
                        documents={managedAccountTncDocuments}
                        acceptedDocs={managedAccountAcceptedDocs}
                        onToggle={handleManagedAccountTncToggle}
                        disabled={asLabel}
                        showError={hasError}
                        error={managedAccountTncError}
                    />
                ) : null}

                <InputLabel label="Signature of Applicant" required />

                <WrapperField
                    disabled={asLabel}
                    jsonField={cyclosFields["signature"]}
                    dataObj={data}
                    onChange={handleChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('signature', errorMsg)}
                />

                <InputLabel required label={"Name of Applicant"} labelStyle={{ fontSize: 15 }} />
                <div className='row g-3 business-v2-step-inline-row'>
                    <div className='col-sm-4'>
                        <WrapperField
                            disabled={asLabel}
                            jsonField={cyclosFields["signerFirstName"]}
                            dataObj={data}
                            onChange={handleChange}
                            showError={hasError}
                            onError={(errorMsg) => handleErrors('signerFirstName', errorMsg)}
                        />
                    </div>
                    <div className='col-sm-4'>
                        <WrapperField
                            disabled={asLabel}
                            jsonField={cyclosFields["signerMiddleName"]}
                            dataObj={data}
                            onChange={handleChange}
                            showError={hasError}
                            onError={(errorMsg) => handleErrors('signerMiddleName', errorMsg)}
                        />
                    </div>
                    <div className='col-sm-4'>
                        <WrapperField
                            disabled={asLabel}
                            jsonField={cyclosFields["signerLastName"]}
                            dataObj={data}
                            onChange={handleChange}
                            showError={hasError}
                            onError={(errorMsg) => handleErrors('signerLastName', errorMsg)}
                        />
                    </div>
                </div>

                {shouldShowBeneficialOwnerCertification ? (
                    <div className='business-v2-step-section mt-3 '>
                        <InputLabel label="Beneficial Owner Certification" required />
                        <div className='business-v2-certification-card'>
                            <label className={`business-v2-step-terms mb-0 ${asLabel ? "is-disabled" : ""}`}>
                                <Input
                                    type="checkbox"
                                    checked={Boolean(data?.tnc)}
                                    disabled={asLabel}
                                    onChange={(event) => {
                                        const checked = event?.target?.checked;
                                        handleErrors('tnc', '');
                                        handleFieldChange('tnc', checked, {
                                            ...data,
                                            tnc: checked,
                                        });
                                    }}
                                />
                                <span className='business-v2-step-terms-copy'>
                                    {certificationTextParts.before}
                                    <button
                                        type="button"
                                        className="business-v2-certification-link"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setCertificateRequestVersion(Date.now());
                                            setShowBeneficialOwnersModal(true);
                                        }}
                                    >
                                        Certificate of Beneficial Owners
                                    </button>
                                    {certificationTextParts.after}
                                </span>
                            </label>
                        </div>
                        {beneficialOwnerCertificationError && hasError ? (
                            <div className="ps-0 mt-1 ms-0 w-100 error__message left-0">
                                {beneficialOwnerCertificationError}
                            </div>
                        ) : null}

                        <Modal
                            size="xl"
                            isOpen={showBeneficialOwnersModal}
                            toggle={() => setShowBeneficialOwnersModal(false)}
                            centered
                        >
                            <ModalHeader toggle={() => setShowBeneficialOwnersModal(false)}>
                                Certificate of Beneficial Owners
                            </ModalHeader>
                            <ModalBody className="fv_doc_field_modal_body">
                                <PDFViewer
                                    modalLoader
                                    pdfStyle={{ height: "calc(80vh - 40px)" }}
                                    src={beneficialOwnersCertificateSrc}
                                    alt="Certificate of Beneficial Owners"
                                />
                            </ModalBody>
                        </Modal>
                    </div>
                ) : null}
            </div>

            {!hideButtons && (
                <div className='business-v2-step-actions'>
                    <ButtonGroupNew
                        nextText={isDocFirst ? "Continue" : undefined}
                        onNextClick={handleSubmit}
                        onBackClick={() => { handlePrev?.() }}
                        step={pageNum}
                        showPageNum={false}
                        pageNum={pageNum}
                        hideBackButton={hideBackButton}
                        hideSubmitButton={hideButtons}
                        asLabel={false}
                        loading={loading || submitting}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(SelfDeclarationV2);
